import { Avatar, Box, Stack, createStyles } from '@mantine/core';
import { useGetPersonas } from '@repo/api-codegen';
import { AIAvatar } from '@repo/common/components/AI/Avatar';
import { Text } from '@repo/foundations';
import { useMemo } from 'react';
import { useAuthUser } from '../../../api';

const useStyles = createStyles(
	(theme, { animationName }: { animationName: string }) => ({
		root: {
			flexGrow: 1,
			height: '100%',
			paddingBottom: theme.spacing.sm,
		},
		emptyStateContainer: {
			flexGrow: 1,
			display: 'flex',
			alignItems: 'center',
		},
		emptyState: {
			padding: 0,
			paddingBottom: theme.other.space[16],
		},
		emptyStateAnimated: {
			[`@keyframes ${animationName}`]: {
				'0%': {
					opacity: 0,
					transform: 'translateY(4px)',
				},
				'100%': {
					opacity: 1,
					transform: 'translateY(0)',
				},
			},
			opacity: 0,
			transform: 'translateY(4px)',
			// eslint-disable-next-line no-custom-animation/no-custom-animation
			animation: `${animationName} 400ms ease forwards 250ms`,
		},
	})
);

export default function AIChatEmpty({
	activeAiPersonaId,
}: {
	activeAiPersonaId: string;
}) {
	const { classes } = useStyles({
		animationName: `fadeUp-${activeAiPersonaId ? activeAiPersonaId.substring(0, 4) : 'default'}`,
	});
	const { user } = useAuthUser();

	const { data: personas } = useGetPersonas({});

	const activePersona = useMemo(
		() =>
			personas?.find((p) =>
				activeAiPersonaId ? p.id === activeAiPersonaId : p.is_default
			),
		[personas, activeAiPersonaId]
	);

	if (!personas) {
		return null;
	}

	return (
		<Stack className={classes.root}>
			<Box className={classes.emptyStateContainer}>
				<Box className={classes.emptyStateAnimated}>
					{activePersona ? (
						<Stack spacing="sm">
							{activePersona?.avatar_url ? (
								<Avatar
									src={activePersona.avatar_url}
									size="md"
									alt={`${activePersona.name} avatar`}
								/>
							) : (
								<AIAvatar size="md" speed="slow" iconSpeed="none" />
							)}
							<Text size="xl" fw={600}>
								{activePersona.name ? activePersona.name : 'New persona'}
							</Text>
							{activePersona.description && (
								<Text size="md">{activePersona.description}</Text>
							)}
						</Stack>
					) : (
						<Stack spacing="sm">
							<AIAvatar size="md" speed="slow" iconSpeed="none" />
							<Stack spacing={0}>
								<Text size="xl" fw={600}>
									Hi, {user?.first_name ?? user?.display_name ?? 'there'}
								</Text>
								<Text size="xl" fw={600} color="text/secondary/default">
									What can I help with?
								</Text>
							</Stack>
						</Stack>
					)}
				</Box>
			</Box>
		</Stack>
	);
}
